import { getBaseTokenList } from '@ui/constants/tokens';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePayTokenBalance } from '@ui/hooks/usePayTokenBalance';
import { Token } from '@ui/types/token';
import BigNumber from 'bignumber.js';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useEffect, useMemo } from 'react';
import { VStack,VstackProps } from 'styled-system/jsx';
import { Address } from 'viem';
import { useAccount } from 'wagmi';

import { ExchangeExecutor, SwapExecutor } from './Executors';
import { formatBigValue } from '@ui/lib/bignumber';

export type ExchangeAmountMode = 'exactIn' | 'exactOut';

export const ExchangeContext = createContext<{
  inputToken?: Token;
  outputToken?: Token;
  inputAmount: string;
  outputAmount: string;
  setInputToken: (token: Token) => void;
  setOutputToken: (token: Token) => void;
  setInputAmount: (amount: string) => void;
  setOutputAmount: (amount: string) => void;
  amountMode: ExchangeAmountMode;
  setAmountMode: (mode: ExchangeAmountMode) => void;

  inputTokens: readonly Token[];
  outputTokens: readonly Token[];

  isQuoteLoading: boolean;
  isSwapPending: boolean;
  onSwap: () => void;
  canSwap: boolean;

  marketPrice?: string;
  isInsufficientBalance: boolean;
}>({
  inputToken: undefined,
  outputToken: undefined,
  inputAmount: '',
  outputAmount: '',
  setInputToken: () => { },
  setOutputToken: () => { },
  setInputAmount: () => { },
  setOutputAmount: () => { },
  amountMode: 'exactIn',
  setAmountMode: () => { },
  inputTokens: [],
  outputTokens: [],
  isQuoteLoading: false,
  isSwapPending: false,
  onSwap: () => { },
  canSwap: false,
  marketPrice: undefined,
  isInsufficientBalance: false,
});

type ExchangeContextProps = {
  children: React.ReactNode;
  label: string;
  defaultInputToken?: Token;
  defaultOutputToken?: Token;
  overrideInputTokens?: Token[];
  overrideOutputTokens?: Token[];
  executor?: ExchangeExecutor;
  /** Called when swap/redeem tx is confirmed (e.g. to refetch dependent data) */
  onSwapSuccess?: () => void;
} & VstackProps;

export const ExchangeProvider = (props: ExchangeContextProps) => {
  const {
    children,
    label,
    defaultInputToken,
    defaultOutputToken,
    overrideInputTokens,
    overrideOutputTokens,
    executor: executorProps,
    onSwapSuccess: onSwapSuccessCallback,
    ...vstackProps
  } = props;

  const executor = executorProps || SwapExecutor;

  const [inputAmount, setInputAmount] = React.useState<string>('');
  const [outputAmount, setOutputAmount] = React.useState<string>('');
  const [amountMode, setAmountMode] = React.useState<ExchangeAmountMode>('exactIn');
  const [inputToken, setInputToken] = React.useState<Token | undefined>(
    defaultInputToken,
  );
  const [outputToken, setOutputToken] = React.useState<Token | undefined>(
    defaultOutputToken,
  );
  const chainId = useDataChainId();
  const { address } = useAccount();
  const queryClient = useQueryClient();

  // Fetch input token balance
  const inputBalance = usePayTokenBalance(inputToken);

  const inputTokens = useMemo(() => {
    let tokens = overrideInputTokens || getBaseTokenList(chainId);

    return tokens
  }, [overrideInputTokens, outputToken, chainId]);

  const outputTokens = useMemo(() => {
    let tokens = overrideOutputTokens || getBaseTokenList(chainId);

    return tokens.filter(
      (t) => t.address?.toLowerCase() !== inputToken?.address?.toLowerCase(),
    );
  }, [overrideOutputTokens, inputToken, chainId]);

  useEffect(() => {
    if (defaultInputToken && defaultInputToken.address !== inputToken?.address) {
      setInputToken(defaultInputToken);
    }
  }, [defaultInputToken?.address]);

  useEffect(() => {
    if (defaultOutputToken && defaultOutputToken.address !== outputToken?.address) {
      setOutputToken(defaultOutputToken);
    }
  }, [defaultOutputToken?.address]);

  // Quote hook for user input
  const { data: quoteData, isLoading: isQuoteLoading } = executor.useQuote({
    inputToken,
    outputToken,
    inputAmount: amountMode === 'exactIn' ? inputAmount : '',
  });

  const { data: quoteExactOutData, isLoading: isQuoteExactOutLoading } =
    executor.useQuoteExactOut?.({
      inputToken,
      outputToken,
      outputAmount: amountMode === 'exactOut' ? outputAmount : '',
    }) ?? { data: null, isLoading: false };

  // Quote hook for market price (1 unit)
  const { data: marketPriceData } = executor.useQuote({
    inputToken,
    outputToken,
    inputAmount: '1',
  });

  // Update output amount when quote changes
  useEffect(() => {
    if (amountMode !== 'exactIn') return;

    if (!quoteData) {
      if (!inputAmount || parseFloat(inputAmount) === 0) setOutputAmount('');
      
return;
    }

    setOutputAmount(
      formatBigValue(quoteData.amountOut, {
        decimals: outputToken?.decimals || 18,
        displayDecimals: 6,
        isUseSeparator: false,
        kmb: false,
        autoKmb: false,
        isTrimTrailingZero: true,
        roundingMode: BigNumber.ROUND_DOWN,
      })
    );
  }, [quoteData, inputAmount]);

  // Update input amount when exact-out quote changes
  useEffect(() => {
    if (amountMode !== 'exactOut') return;

    if (!quoteExactOutData) {
      if (!outputAmount || parseFloat(outputAmount) === 0) setInputAmount('');
      
return;
    }

    const amount = parseFloat(quoteExactOutData.formattedAmountIn);
    setInputAmount(
      amount.toLocaleString(undefined, {
        maximumFractionDigits: 8,
        useGrouping: false,
      }),
    );
  }, [quoteExactOutData, outputAmount, amountMode]);

  // Swap hook
  const { mutate: swap, isPending: isSwapPending } = executor.useSwap({
    onSuccess: () => {
      setInputAmount('');
      setOutputAmount('');
      setAmountMode('exactIn');
      // Refresh active queries immediately after confirmed tx
      // so balances/caps/positions update without waiting for polling.
      void queryClient.invalidateQueries();
      onSwapSuccessCallback?.();
    }
  });

  const handleSwap = () => {
    if (!inputToken || !outputToken || !address) return;
    if (amountMode === 'exactIn' && !quoteData) return;
    if (amountMode === 'exactOut' && !quoteExactOutData) return;

    const args =
      amountMode === 'exactIn'
        ? executor.getSwapArgs({
          inputToken,
          outputToken,
          inputAmount,
          quoteData: quoteData!,
          address: address as Address,
        })
        : {
          inputToken,
          outputToken,
          inputAmount,
          // In exact-out mode, treat user-entered outputAmount as a strict minOut.
          minOutputAmount: outputAmount,
          adapter: quoteExactOutData!.bestCandidate.adapter,
          adapterData: quoteExactOutData!.bestCandidate.data,
          receiver: address as Address,
        };

    swap(args);
  };

  // Check for insufficient balance
  const isInsufficientBalance = useMemo(() => {
    if (!inputAmount || !inputBalance) return false;

    return BigNumber(inputAmount).gt(inputBalance);
  }, [inputAmount, inputBalance]);

  const canSwap = !!(
    inputToken &&
    outputToken &&
    ((amountMode === 'exactIn' &&
      inputAmount &&
      parseFloat(inputAmount) > 0 &&
      quoteData && quoteData.amountOut > 0n) ||
      (amountMode === 'exactOut' &&
        outputAmount &&
        parseFloat(outputAmount) > 0 &&
        quoteExactOutData &&
        inputAmount &&
        parseFloat(inputAmount) > 0)) &&
    address &&
    !isInsufficientBalance
  );

  const isQuoteLoadingCombined =
    amountMode === 'exactIn' ? isQuoteLoading : isQuoteExactOutLoading;

  return (
    <ExchangeContext.Provider
      value={{
        inputToken,
        outputToken,
        inputAmount,
        outputAmount,
        setInputToken,
        setOutputToken,
        setInputAmount,
        setOutputAmount,
        amountMode,
        setAmountMode,
        inputTokens: inputTokens,
        outputTokens: outputTokens,
        isQuoteLoading: isQuoteLoadingCombined,
        isSwapPending,
        onSwap: handleSwap,
        canSwap,
        marketPrice: marketPriceData?.formattedAmountOut,
        isInsufficientBalance,
      }}
    >
      <VStack
        w="full"
        h="full"
        justifyContent="space-between"
        {...vstackProps}
      >{props.children}</VStack>
    </ExchangeContext.Provider>
  );
};
